/**
 * HotelCardItem — premium hotel result card supporting both grid and list
 * layouts. List layout (default on desktop) has image-left / content-middle /
 * price-right composition à la Booking.com / Agoda. Grid layout (mobile or
 * user toggle) keeps the compact stacked card.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import {
  Star, MapPin, BadgeCheck, Building2, ArrowRight,
  Wifi, Coffee, Car, Snowflake, Utensils, Dumbbell, Waves, Tv,
} from 'lucide-react';

const _Stars = ({ rating }) => {
  if (!rating) return null;
  const r = Math.round(rating);
  return (
    <div className="flex items-center gap-0.5">
      {Array.from({ length: 5 }, (_, i) => (
        <Star key={i} className={`w-3 h-3 ${i < r ? 'fill-amber-400 text-amber-400' : 'text-gray-300'}`} />
      ))}
    </div>
  );
};

// Map common amenity keywords → lucide icons (rendered only when supplier sent
// the amenity name; otherwise the row is silently skipped — no hardcoded chips)
const AMENITY_ICONS = [
  { match: /wi-?fi|internet/i, Icon: Wifi, label: 'WiFi' },
  { match: /breakfast|tea|coffee/i, Icon: Coffee, label: 'Breakfast' },
  { match: /parking|valet/i, Icon: Car, label: 'Parking' },
  { match: /a\/?c|air.?cond/i, Icon: Snowflake, label: 'AC' },
  { match: /restaurant|dining|food/i, Icon: Utensils, label: 'Restaurant' },
  { match: /gym|fitness/i, Icon: Dumbbell, label: 'Gym' },
  { match: /pool|swim/i, Icon: Waves, label: 'Pool' },
  { match: /tv|television/i, Icon: Tv, label: 'TV' },
];

const _amenityIcons = (amenities) => {
  if (!Array.isArray(amenities)) return [];
  const out = [];
  const seen = new Set();
  for (const a of amenities) {
    const str = String(a).trim();
    if (!str) continue;
    for (const { match, Icon, label } of AMENITY_ICONS) {
      if (match.test(str) && !seen.has(label)) {
        out.push({ Icon, label });
        seen.add(label);
        break;
      }
    }
    if (out.length >= 4) break;
  }
  return out;
};

const HotelCardItem = ({ hotel, sessionId, view, locality, cityName, forwardParams }) => {
  const price = hotel.starting_price;
  const amenityIcons = _amenityIcons(hotel.amenities);
  const hasImage = !!hotel.images?.[0];
  const linkTo = `/hotels/${encodeURIComponent(hotel.id)}?${forwardParams || `sessionId=${encodeURIComponent(sessionId)}`}`;

  if (view === 'grid') {
    return (
      <Link
        to={linkTo}
        className="block bg-white rounded-2xl shadow-sm hover:shadow-lg border border-gray-200 hover:border-[#659A9B]/40 transition-all overflow-hidden group"
        data-testid={`hotel-result-card-${hotel.id}`}
      >
        <div className="aspect-[4/3] bg-gray-100 relative overflow-hidden">
          {hasImage ? (
            <img
              src={hotel.images[0]}
              alt={hotel.name}
              loading="lazy"
              className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
              onError={(e) => { e.currentTarget.style.display = 'none'; }}
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-[#1B375B]/10 to-[#659A9B]/15">
              <Building2 className="w-12 h-12 text-[#1B375B]/30" />
            </div>
          )}
          {hotel.refundable === true && (
            <div className="absolute top-2 left-2 px-2 py-0.5 bg-emerald-500/95 text-white rounded-full text-[10px] font-bold flex items-center gap-1 shadow-md">
              <BadgeCheck className="w-3 h-3" /> Free Cancellation
            </div>
          )}
          {hotel.rating >= 4 && (
            <div className="absolute top-2 right-2 px-1.5 py-0.5 bg-amber-500/95 text-white rounded text-[10px] font-extrabold shadow-md flex items-center gap-0.5">
              <Star className="w-2.5 h-2.5 fill-white" /> {hotel.rating}
            </div>
          )}
        </div>

        <div className="p-3 sm:p-4">
          <h3 className="text-sm sm:text-base font-bold text-[#1B375B] line-clamp-2 mb-1" style={{ fontFamily: 'Manrope, sans-serif' }}>
            {hotel.name}
          </h3>
          <_Stars rating={hotel.rating} />
          {(cityName || locality) && (
            <div className="flex items-center gap-1 mt-1 text-xs text-gray-500" data-testid={`hotel-card-location-${hotel.id}`}>
              <MapPin className="w-3 h-3 shrink-0" />
              <span className="truncate">
                {locality ? <><span className="font-semibold text-gray-700">{locality}</span>{cityName ? <span className="text-gray-400">, {cityName}</span> : null}</> : cityName}
              </span>
            </div>
          )}
          {amenityIcons.length > 0 && (
            <div className="flex items-center gap-2 mt-2 text-gray-400">
              {amenityIcons.map(({ Icon, label }) => (
                <span key={label} title={label}><Icon className="w-3.5 h-3.5" /></span>
              ))}
            </div>
          )}
          <div className="mt-3 pt-3 border-t border-gray-100 flex items-end justify-between">
            <div>
              <p className="text-[10px] text-gray-400 uppercase tracking-wider font-bold">From</p>
              <p className="text-lg font-extrabold text-[#1B375B]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                ₹{price?.toLocaleString('en-IN', { maximumFractionDigits: 0 }) ?? '—'}
              </p>
              <p className="text-[10px] text-gray-400">per night</p>
            </div>
            <span className="text-xs font-bold text-[#659A9B] group-hover:underline">View →</span>
          </div>
        </div>
      </Link>
    );
  }

  // ── List view (default desktop) ──
  return (
    <Link
      to={linkTo}
      className="bg-white rounded-2xl shadow-sm hover:shadow-lg border border-gray-200 hover:border-[#659A9B]/40 transition-all overflow-hidden grid grid-cols-1 sm:grid-cols-[260px_1fr] group"
      data-testid={`hotel-result-card-${hotel.id}`}
    >
      <div className="aspect-[4/3] sm:aspect-auto sm:min-h-[200px] bg-gray-100 relative overflow-hidden">
        {hasImage ? (
          <img
            src={hotel.images[0]}
            alt={hotel.name}
            loading="lazy"
            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
            onError={(e) => { e.currentTarget.style.display = 'none'; }}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-[#1B375B]/10 to-[#659A9B]/15">
            <Building2 className="w-12 h-12 text-[#1B375B]/30" />
          </div>
        )}
        {hotel.refundable === true && (
          <div className="absolute top-2 left-2 px-2 py-0.5 bg-emerald-500/95 text-white rounded-full text-[10px] font-bold flex items-center gap-1 shadow-md">
            <BadgeCheck className="w-3 h-3" /> Free Cancellation
          </div>
        )}
      </div>

      <div className="flex flex-col p-4 gap-2">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <h3 className="text-base sm:text-lg font-extrabold text-[#1B375B] line-clamp-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {hotel.name}
            </h3>
            <div className="flex items-center gap-2 mt-1">
              <_Stars rating={hotel.rating} />
              {hotel.rating >= 4 && (
                <span className="px-1.5 py-0.5 bg-amber-100 text-amber-800 text-[10px] font-extrabold rounded">EXCELLENT</span>
              )}
            </div>
            {(cityName || locality) && (
              <div className="flex items-center gap-1 mt-1.5 text-xs text-gray-600" data-testid={`hotel-card-location-${hotel.id}`}>
                <MapPin className="w-3.5 h-3.5 text-[#659A9B] shrink-0" />
                <span className="truncate">
                  {locality ? <><span className="font-semibold text-[#1B375B]">{locality}</span>{cityName ? <span className="text-gray-400">, {cityName}</span> : null}</> : cityName}
                </span>
              </div>
            )}
          </div>

          <div className="text-right shrink-0 hidden sm:block">
            <p className="text-[10px] text-gray-400 uppercase tracking-wider font-bold">From</p>
            <p className="text-2xl font-extrabold text-[#1B375B] leading-none" style={{ fontFamily: 'Outfit, sans-serif' }}>
              ₹{price?.toLocaleString('en-IN', { maximumFractionDigits: 0 }) ?? '—'}
            </p>
            <p className="text-[10px] text-gray-400 mt-0.5">per night</p>
            <p className="text-[10px] text-gray-400">+ taxes & fees</p>
          </div>
        </div>

        {amenityIcons.length > 0 && (
          <div className="flex items-center flex-wrap gap-2 mt-1 text-gray-500">
            {amenityIcons.map(({ Icon, label }) => (
              <span key={label} className="inline-flex items-center gap-1 px-2 py-0.5 bg-gray-50 rounded-full text-[10px] font-semibold">
                <Icon className="w-3 h-3" /> {label}
              </span>
            ))}
          </div>
        )}

        <div className="mt-auto pt-2 flex items-end justify-between gap-2 border-t border-gray-100">
          <div className="text-right sm:hidden">
            <p className="text-[10px] text-gray-400 uppercase font-bold">From</p>
            <p className="text-xl font-extrabold text-[#1B375B]" style={{ fontFamily: 'Outfit, sans-serif' }}>
              ₹{price?.toLocaleString('en-IN', { maximumFractionDigits: 0 }) ?? '—'}
            </p>
          </div>
          <span className="ml-auto inline-flex items-center gap-1.5 px-4 py-2 bg-[#1B375B] text-white rounded-xl text-xs font-bold group-hover:bg-[#659A9B] transition-colors">
            See Rooms <ArrowRight className="w-3.5 h-3.5" />
          </span>
        </div>
      </div>
    </Link>
  );
};

export const HotelCardSkeleton = ({ view = 'list' }) => {
  if (view === 'grid') {
    return (
      <div className="bg-white rounded-2xl shadow-sm overflow-hidden animate-pulse">
        <div className="aspect-[4/3] bg-gray-200" />
        <div className="p-3 space-y-2">
          <div className="h-4 bg-gray-200 rounded w-3/4" />
          <div className="h-3 bg-gray-200 rounded w-1/3" />
          <div className="h-3 bg-gray-200 rounded w-1/2" />
          <div className="h-6 bg-gray-200 rounded w-1/3 mt-3" />
        </div>
      </div>
    );
  }
  return (
    <div className="bg-white rounded-2xl shadow-sm grid sm:grid-cols-[260px_1fr] overflow-hidden animate-pulse">
      <div className="aspect-[4/3] sm:aspect-auto sm:min-h-[200px] bg-gray-200" />
      <div className="p-4 space-y-2">
        <div className="h-5 bg-gray-200 rounded w-3/4" />
        <div className="h-3 bg-gray-200 rounded w-1/3" />
        <div className="h-3 bg-gray-200 rounded w-1/2" />
        <div className="h-6 bg-gray-200 rounded w-1/4 mt-2" />
        <div className="h-9 bg-gray-200 rounded w-32 mt-3 ml-auto" />
      </div>
    </div>
  );
};

export default HotelCardItem;
